/**
 * ChatAvatar — Avatar com imagem ou fallback de iniciais.
 * Usa os componentes existentes de ui/avatar.
 */

import React from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

export interface ChatAvatarProps {
  name?: string;
  avatarUrl?: string | null;
  /** Tamanho: 'sm' (28px) para inline, 'md' (36px) padrão */
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Gera as iniciais a partir do nome (até 2 caracteres).
 */
function getInitials(name?: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (name[0] ?? '?').toUpperCase();
}

const sizeClasses = {
  sm: 'size-7 text-[10px]',
  md: 'size-9 text-xs',
} as const;

export function ChatAvatar({ name, avatarUrl, size = 'md', className }: ChatAvatarProps) {
  const initials = getInitials(name);

  return (
    <Avatar className={cn(sizeClasses[size], 'shrink-0', className)}>
      {avatarUrl && (
        <AvatarImage src={avatarUrl} alt={name ?? 'Avatar'} />
      )}
      <AvatarFallback className={cn(sizeClasses[size], 'font-medium')}>
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}
